import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Share2, CheckCircle2, RotateCcw, X, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGetQuizByIdQuery,
  useUpdateQuizMutation,
  useUploadQuizCoverMutation,
  useDeleteQuizCoverMutation,
} from '../../api/apiSlice';
import QuizEditor from '../../components/quiz/QuizEditor';
import ShareModal from '../../components/quiz/ShareModal';
import ExpirationBadge from '../../components/quiz/ExpirationBadge';
import CoverPicker from '../../components/quiz/CoverPicker';
import { useDraftAutosave, formatAgo } from '../../lib/useDraftAutosave';

// Edit reuse: тот же QuizEditor, что и для создания. Просто прелоадим данные.
const QuizEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetQuizByIdQuery(id);
  const [updateQuiz, { isLoading: isSaving }] = useUpdateQuizMutation();
  const [uploadCover, { isLoading: isUploadingCover }] = useUploadQuizCoverMutation();
  const [deleteCover, { isLoading: isDeletingCover }] = useDeleteQuizCoverMutation();
  const [value, setValue] = useState(null);
  const [showShare, setShowShare] = useState(false);

  // Автосейв в localStorage — спасает от потери ввода при смене вкладки.
  // Ключ включает quizId, чтобы черновики разных квизов не пересекались.
  // shouldSave: только когда value уже инициализирован (после loadа).
  const { restored, restore, dismissRestore, clear, savedAt } = useDraftAutosave(
    `quiz_edit_${id}`,
    value,
    { shouldSave: (v) => v !== null }
  );

  const handleUploadCover = async (file) => {
    try {
      await uploadCover({ id, coverFile: file }).unwrap();
      toast.success('Обложка обновлена');
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось загрузить обложку');
    }
  };
  const handleDeleteCover = async () => {
    try {
      await deleteCover(id).unwrap();
      toast.success('Обложка удалена');
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось удалить обложку');
    }
  };

  useEffect(() => {
    if (!data?.quiz) return;
    setValue({
      title: data.quiz.title || '',
      description: data.quiz.description || '',
      questions: (data.quiz.questions || []).map((q) => ({
        text: q.text,
        options: q.options.map((o) => ({ text: o.text })),
        correctOptionIndex: q.correctOptionIndex,
        explanation: q.explanation || '',
      })),
    });
  }, [data]);

  const submit = async () => {
    try {
      await updateQuiz({ id, ...value }).unwrap();
      // Успешно сохранили на бэк → стираем локальный черновик.
      clear();
      toast.success('Сохранено');
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось сохранить');
    }
  };

  if (isLoading || !value) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
        {error?.data?.message || 'Не удалось загрузить'}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <Link
        to="/dashboard/student/quizzes"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> К моим квизам
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white break-words">
            Редактирование квиза
          </h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <ExpirationBadge
              expiresAt={data.quiz.expiresAt}
              status={data.quiz.status}
            />
            {savedAt && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                Черновик сохранён {formatAgo(savedAt)}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowShare(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 text-sm font-medium transition"
          >
            <Share2 className="w-4 h-4" /> Поделиться
          </button>
        </div>
      </div>

      {/* Banner — есть несохранённый черновик локально. */}
      {restored && (
        <div className="mb-5 rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-950/20 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
              <Save className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                Есть несохранённый черновик
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                Вы что-то меняли в этом квизе ({formatAgo(restored.savedAt)}) но не сохранили. Восстановить?
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  setValue(restored.value);
                  restore();
                }}
                className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-xs font-semibold transition"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Восстановить
              </button>
              <button
                type="button"
                onClick={dismissRestore}
                className="inline-flex items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1.5 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                title="Отбросить черновик"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <CoverPicker
          mode="immediate"
          serverUrl={data.quiz.coverImageWebpUrl}
          onUpload={handleUploadCover}
          onRemoveServer={handleDeleteCover}
          isUploading={isUploadingCover || isDeletingCover}
        />

        <QuizEditor
          value={value}
          onChange={setValue}
          onSubmit={submit}
          isSubmitting={isSaving}
          submitLabel="Сохранить изменения"
        />
      </div>

      {showShare && (
        <ShareModal
          quizId={id}
          quizTitle={data.quiz.title}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  );
};

export default QuizEditPage;
